import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { ChevronDown } from 'lucide-react';
import { api } from '../api/client';
import { useFeature } from '../lib/features';

// Single dropdown that lists:
//   1. The default RankOnAI prompt (always)
//   2. Built-in DFY templates (when user has dfyContentLibrary feature)
//   3. The user's saved custom prompts (when user has customAiPrompts feature)
//
// Selection is communicated to the parent as a body payload — either
// `{}` (default), `{ promptTemplateId }`, or `{ customPromptId }` — ready
// to spread into the rewrite request.
//
// Props:
//   value:   `{ kind: 'default'|'builtin'|'custom', id?: string }`
//   onChange:(value) => void
export default function RewriterPromptPicker({ value, onChange, label = 'Voice / tone' }) {
  const hasDfy = useFeature('dfyContentLibrary');
  const hasCustom = useFeature('customAiPrompts');

  const builtinQuery = useQuery({
    queryKey: ['dfy-rewriter-prompts-picker'],
    queryFn: () =>
      api.get('/api/dfy/rewriter-prompts?limit=100').then((r) => r.data.rows || []),
    enabled: hasDfy,
    retry: false,
  });

  const customQuery = useQuery({
    queryKey: ['user-rewriter-prompts-picker'],
    queryFn: () =>
      api.get('/api/me/rewriter-prompts').then((r) => r.data.prompts || []),
    enabled: hasCustom,
    retry: false,
  });

  const options = useMemo(() => {
    const out = [{ key: 'default', label: 'Default — RankOnAI baseline (GEO-optimized)' }];
    const customs = customQuery.data || [];
    if (customs.length) {
      out.push({ key: 'group-custom', group: 'Your saved prompts' });
      for (const p of customs) {
        out.push({
          key: `custom:${p._id}`,
          label: `${p.name}${p.voiceTone ? ` · ${p.voiceTone}` : ''}`,
        });
      }
    }
    const builtins = builtinQuery.data || [];
    if (builtins.length) {
      out.push({ key: 'group-builtin', group: 'Booster Pack library' });
      for (const t of builtins) {
        out.push({
          key: `builtin:${t._id}`,
          label: `${t.name}${t.voiceTone ? ` · ${t.voiceTone}` : ''}`,
        });
      }
    }
    return out;
  }, [builtinQuery.data, customQuery.data]);

  // Map the picker key to a stable React-select-style value string.
  const stringValue =
    value?.kind === 'custom' && value.id
      ? `custom:${value.id}`
      : value?.kind === 'builtin' && value.id
        ? `builtin:${value.id}`
        : 'default';

  function handleChange(e) {
    const v = e.target.value;
    if (v === 'default') return onChange({ kind: 'default' });
    if (v.startsWith('custom:')) return onChange({ kind: 'custom', id: v.slice(7) });
    if (v.startsWith('builtin:')) return onChange({ kind: 'builtin', id: v.slice(8) });
  }

  // If user has neither feature there's only the default option — render as
  // disabled, with an inline hint that links upward.
  if (!hasDfy && !hasCustom) {
    return (
      <label className="text-xs text-ink-500">
        <span className="block mb-1 uppercase tracking-wide">{label}</span>
        <div className="input bg-gray-50 text-ink-500 flex items-center justify-between cursor-not-allowed">
          <span>Default — RankOnAI baseline</span>
          <span className="text-[10px] text-ink-400">Upgrade for more voices</span>
        </div>
      </label>
    );
  }

  return (
    <label className="text-xs text-ink-500">
      <span className="block mb-1 uppercase tracking-wide">{label}</span>
      <div className="relative">
        <select
          className="input pr-8 appearance-none"
          value={stringValue}
          onChange={handleChange}
        >
          {options.map((o) =>
            o.group ? (
              <option key={o.key} disabled>
                ── {o.group} ──
              </option>
            ) : (
              <option key={o.key} value={o.key}>
                {o.label}
              </option>
            ),
          )}
        </select>
        <ChevronDown className="absolute right-2 top-1/2 -translate-y-1/2 w-4 h-4 text-ink-400 pointer-events-none" />
      </div>
    </label>
  );
}

// Convert the picker value into the body payload the rewrite endpoint expects.
export function pickerValueToBody(value) {
  if (!value || value.kind === 'default') return {};
  if (value.kind === 'custom' && value.id) return { customPromptId: value.id };
  if (value.kind === 'builtin' && value.id) return { promptTemplateId: value.id };
  return {};
}
